import {
	useAddQuoteProductCommentMutation,
	useDeleteQuoteProductCommentMutation,
	useEditQuoteProductCommentMutation,
	useGenerateQuoteProductCommentUploadUrlMutation,
	useGetQuoteProductCommentsQuery,
	useGetQuoteProductMentionCandidatesQuery,
	useMarkQuoteProductThreadReadMutation,
	useOnQuoteProductThreadUpdatedSubscription,
	useSubscriptionRestored,
	type GetQuoteProductCommentsQuery,
} from '@codezo/graphql';
import type { PendingAttachment } from '@codezo/product-comments';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { prepareFiles } from '../lib/file-size';

export type ProductComment = GetQuoteProductCommentsQuery['getQuoteProductComments'][number];

const WEB_IMAGE = /^image\/(jpeg|png|gif|webp|avif|bmp|svg\+xml)$/i;

interface PendingFile extends PendingAttachment {
	file: File;
}

/**
 * Everything one product thread needs — messages, who can be tagged, posting
 * with files, editing, deleting, read state, and live updates while it's open.
 *
 * Kept identical in the admin and vetter apps (only import paths differ). The
 * thread is `orderQuoteItemId` + `vetterEntityId`; leave the supplier out for
 * studio notes, and a supplier session may always leave it out.
 */
export function useProductThread({
	orderQuoteItemId,
	vetterEntityId,
	skip = false,
	onRead,
}: {
	orderQuoteItemId: string;
	vetterEntityId?: string | null;
	skip?: boolean;
	/** After the thread is marked read — the caller clears its badge. */
	onRead?: () => void;
}) {
	const input = { orderQuoteItemId, vetterEntityId: vetterEntityId ?? undefined };

	const { data, loading, refetch } = useGetQuoteProductCommentsQuery({
		variables: { input },
		fetchPolicy: 'no-cache',
		skip,
	});
	const { data: candidateData } = useGetQuoteProductMentionCandidatesQuery({
		variables: { input },
		fetchPolicy: 'no-cache',
		skip,
	});

	/* Held locally: a sent message appears from the mutation's response, and
	   the live stream adds, edits and deletes in place. Keyed on id throughout —
	   your own post arrives twice (response and ADDED event). */
	const [messages, setMessages] = useState<ProductComment[]>([]);
	useEffect(() => {
		if (data) setMessages(data.getQuoteProductComments);
	}, [data]);
	const upsert = useCallback(
		(comment: ProductComment) =>
			setMessages((prev) =>
				prev.some((m) => m.id === comment.id)
					? prev.map((m) => (m.id === comment.id ? comment : m))
					: [...prev, comment]
			),
		[]
	);

	/* Live while open. The server ENDS the stream when the viewer loses access
	   (a revoked link, a withdrawn request) — but a stream can also end for
	   reasons that aren't that: an error on the way, a token that expired while
	   the page sat open. So an end is checked, never assumed: the thread is
	   fetched again, and only a refused fetch makes it read-only. A thread that
	   still loads subscribes again, backing off like the transport does. */
	const [ended, setEnded] = useState(false);
	const [paused, setPaused] = useState(false);
	const retries = useRef(0);
	const resumeTimer = useRef<ReturnType<typeof setTimeout>>();
	useEffect(() => () => clearTimeout(resumeTimer.current), []);

	const checkAccess = useCallback(async () => {
		setPaused(true);
		const res = await refetch().catch(() => null);
		const stillOpen = !!res?.data?.getQuoteProductComments && !res.errors?.length;
		if (!stillOpen) {
			setEnded(true);
			return;
		}
		const delay = Math.min(30_000, 1000 * 2 ** retries.current);
		retries.current += 1;
		resumeTimer.current = setTimeout(() => setPaused(false), delay);
	}, [refetch]);

	// Re-opened after a backend restart: fetch once for what arrived in the gap.
	useSubscriptionRestored(() => void refetch(), ['OnQuoteProductThreadUpdated']);

	useOnQuoteProductThreadUpdatedSubscription({
		variables: { input },
		skip: skip || ended || paused,
		onData: ({ data: event }) => {
			retries.current = 0;
			const update = event.data?.quoteProductThreadUpdated;
			// ADDED appends; EDITED and DELETED (arriving already emptied) replace.
			if (update) upsert(update.comment);
		},
		onComplete: () => void checkAccess(),
		onError: () => void checkAccess(),
	});

	const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
	const [preparing, setPreparing] = useState(false);
	const [sending, setSending] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);

	const [generateUrl] = useGenerateQuoteProductCommentUploadUrlMutation();
	const [addComment] = useAddQuoteProductCommentMutation();
	const [editComment, { loading: editSaving }] = useEditQuoteProductCommentMutation();
	const [deleteComment] = useDeleteQuoteProductCommentMutation();
	const [markRead] = useMarkQuoteProductThreadReadMutation();

	const addFiles = async (files: FileList) => {
		setPreparing(true);
		try {
			const prepared = await prepareFiles(files);
			setPendingFiles((prev) => [
				...prev,
				...prepared.map((file) => ({
					file,
					name: file.name,
					size: file.size,
					preview: WEB_IMAGE.test(file.type) ? URL.createObjectURL(file) : '',
				})),
			]);
		} finally {
			setPreparing(false);
		}
	};

	const removeFile = (index: number) =>
		setPendingFiles((prev) => {
			const gone = prev[index];
			if (gone?.preview) URL.revokeObjectURL(gone.preview);
			return prev.filter((_, i) => i !== index);
		});

	/** Files first, one upload each; then the message with their blob urls. */
	const send = async (body: string): Promise<boolean> => {
		setSending(true);
		try {
			const attachments = [];
			for (const { file } of pendingFiles) {
				const { data: urlData } = await generateUrl({
					variables: {
						input: {
							...input,
							fileName: file.name,
							fileType: file.type || 'application/octet-stream',
							fileSize: file.size,
						},
					},
				});
				const upload = urlData?.generateQuoteProductCommentUploadUrl;
				if (!upload?.success || !upload.uploadUrl || !upload.blobUrl) {
					throw new Error(upload?.message || `Could not upload ${file.name}`);
				}
				const res = await fetch(upload.uploadUrl, {
					method: 'PUT',
					body: file,
					headers: {
						'x-ms-blob-type': 'BlockBlob',
						'Content-Type': file.type || 'application/octet-stream',
					},
				});
				if (!res.ok) throw new Error(`Could not upload ${file.name}`);
				attachments.push({
					blobUrl: upload.blobUrl,
					fileName: file.name,
					fileType: file.type || 'application/octet-stream',
					fileSize: file.size,
				});
			}

			const { data: added } = await addComment({
				variables: {
					input: { ...input, body: body || undefined, attachments },
				},
			});
			const res = added?.addQuoteProductComment;
			if (!res?.success || !res.comment) {
				throw new Error(res?.message || 'Your message could not be sent.');
			}
			upsert(res.comment);
			pendingFiles.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
			setPendingFiles([]);
			return true;
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Your message could not be sent.');
			return false;
		} finally {
			setSending(false);
		}
	};

	const saveEdit = async (id: string, body: string) => {
		try {
			const { data: res } = await editComment({ variables: { id, body } });
			if (!res?.editQuoteProductComment.success) {
				throw new Error(res?.editQuoteProductComment.message || 'Could not save the edit.');
			}
			setEditingId(null);
			await refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not save the edit.');
		}
	};

	const remove = async (id: string) => {
		try {
			const { data: res } = await deleteComment({ variables: { id } });
			if (!res?.deleteQuoteProductComment.success) {
				throw new Error(res?.deleteQuoteProductComment.message || 'Could not delete it.');
			}
			await refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not delete it.');
		}
	};

	/* Once per newest message: reaching the bottom again without anything new
	   has nothing left to mark. */
	const markedUpTo = useRef<string | null>(null);
	const lastId = messages[messages.length - 1]?.id ?? null;
	const reachedBottom = useCallback(() => {
		// Only while it's actually being looked at — a background tab reads nothing.
		if (skip || !lastId || markedUpTo.current === lastId || document.hidden) return;
		markedUpTo.current = lastId;
		markRead({
			variables: { input: { orderQuoteItemId, vetterEntityId: vetterEntityId ?? undefined } },
		})
			.then(() => onRead?.())
			.catch(() => {
				markedUpTo.current = null;
			});
	}, [skip, lastId, markRead, orderQuoteItemId, vetterEntityId, onRead]);

	// Coming back to the tab catches up on what arrived while it was hidden.
	useEffect(() => {
		const onVisible = () => {
			if (!document.hidden) reachedBottom();
		};
		document.addEventListener('visibilitychange', onVisible);
		return () => document.removeEventListener('visibilitychange', onVisible);
	}, [reachedBottom]);

	return {
		messages,
		/** The server closed the live stream — the viewer can no longer open this thread. */
		ended,
		loading: loading && messages.length === 0,
		candidates: candidateData?.getQuoteProductMentionCandidates ?? [],
		pendingFiles,
		preparing,
		addFiles,
		removeFile,
		send,
		sending,
		editingId,
		setEditingId,
		saveEdit,
		editSaving,
		remove,
		reachedBottom,
	};
}
